import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { Activity, Users, Flame, ShieldCheck, Recycle, Phone, MessageSquare, Target, Bot } from 'lucide-react';
import { apiGet } from '../lib/api.js';
import { fmtMoney, fmtPct } from '../lib/format.js';

function AiStatusPill() {
  const { data } = useQuery({
    queryKey: ['ai-health'],
    queryFn: () => apiGet('/ai/health').catch(() => ({ aiReady: false })),
    refetchInterval: 30000,
  });
  const ready = !!data?.aiReady;
  return (
    <div
      title={data?.note || 'AI status'}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
        ready
          ? 'bg-emerald-500/10 border-emerald-400/30 text-emerald-300'
          : 'bg-amber-500/10 border-amber-400/30 text-amber-300'
      }`}
    >
      <Bot className="w-3.5 h-3.5" />
      {ready ? `AI live — ${data?.model || 'gpt-4o-mini'}` : 'AI fallback (set AI_API_KEY)'}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent = 'purple' }) {
  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-white/50 uppercase tracking-wider">{label}</div>
        <Icon className={`w-4 h-4 text-civix-${accent === 'cyan' ? 'cyan' : 'purpleSoft'}`}/>
      </div>
      <div className="stat-num gradient-text mt-2">{value}</div>
    </div>
  );
}

export default function Admin() {
  const [sim, setSim] = useState(false);
  const { data } = useQuery({
    queryKey: ['overwatch', sim],
    queryFn: () => apiGet('/admin/overwatch', { sim }),
    refetchInterval: 10000,
  });
  const m = data?.metrics || {};
  const revenue = data?.dailyRevenue || [];
  const leaderboard = data?.leaderboard || [];
  const campaigns = data?.campaigns || [];

  return (
    <div className="space-y-4 max-w-[1500px] mx-auto">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs flex items-center gap-2">
            <ShieldCheck className="w-3 h-3"/>Admin Overwatch
          </div>
          <h1 className="font-display text-3xl font-bold mt-1">Mission Control</h1>
        </div>
        <div className="flex items-center gap-3">
          <AiStatusPill />
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={sim} onChange={(e) => setSim(e.target.checked)}/>
            Simulation Mode
          </label>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Total Leads" value={m.totalLeads || 0}/>
        <MetricCard icon={Target} label="Sold" value={m.soldLeads || 0}/>
        <MetricCard icon={Activity} label="Conversion" value={fmtPct(m.conversion)}/>
        <MetricCard icon={Flame} label="Hot Leads" value={m.hotLeads || 0}/>
        <MetricCard icon={Phone} label="Calls Today" value={m.todaysCalls || 0} accent="cyan"/>
        <MetricCard icon={MessageSquare} label="SMS Today" value={m.todaysSms || 0} accent="cyan"/>
        <MetricCard icon={Activity} label="Active Agents" value={m.activeAgents || 0} accent="cyan"/>
        <MetricCard icon={Recycle} label="Lead Pool" value={m.recycledLeads || 0}/>
      </div>

      <div className="panel p-5">
        <div className="font-display font-bold mb-3">Revenue (14 Days)</div>
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={revenue}>
              <defs>
                <linearGradient id="rev2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.6}/>
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="_id" stroke="#666" fontSize={10}/>
              <YAxis stroke="#666" fontSize={10}/>
              <Tooltip contentStyle={{ background: '#10102a', border: '1px solid #252550' }}/>
              <Area dataKey="revenue" stroke="#22d3ee" fill="url(#rev2)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="font-display font-bold mb-3">Top Agents</div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-white/50 text-left">
              <tr><th className="py-2">Agent</th><th>Sales</th><th>Revenue</th><th>Close</th></tr>
            </thead>
            <tbody>
              {leaderboard.map((a) => (
                <tr key={a._id} className="border-t border-civix-border/50">
                  <td className="py-3 font-medium">{a.name}</td>
                  <td>{a.totalSales}</td>
                  <td>{fmtMoney(a.totalRevenue)}</td>
                  <td className="gradient-text">{fmtPct(a.closeRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel p-5">
          <div className="font-display font-bold mb-3">Campaigns</div>
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div key={c._id} className="panel-light p-3">
                <div className="flex justify-between">
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-white/50">{c.productType} · {c.funnelType}</div>
                  </div>
                  <span className={`pill ${c.status === 'active' ? 'pill-sold' : 'pill-cold'}`}>{c.status}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mt-2 text-white/70">
                  <div>Leads: {c.performance?.totalLeads || 0}</div>
                  <div>Sales: {c.performance?.totalSales || 0}</div>
                  <div>Rev: {fmtMoney(c.performance?.totalRevenue)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
